import React, {useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import Fetch from '../../../../Fetch/fetch';
import ISA315RelateItProcessItAppList from './ISA315RelateItProcessItAppList';
import NoDataPlaceholder from '../../../../ResourceWithLink/NoDataPlaceholder';
import BodySpecialToolbar from './BodySpecialToolbar';
import {clientSideEventNames, entities, formBodyType} from '../../../../../../util/uiconstants';
import {labels} from '../../../../../../util/utils';
import UnassociatedEntityCount from './UnassociatedEntityCount';
import {clientSideSearch} from '../../../../../../actions/BodySpecial/bodySpecialActions';
import NewItApplicationToolbarButton from './ISA315ItProcessItApplication/NewItApplicationToolbarButton';
import NewItProcessToolbarButton from './ISA315ItProcessItApplication/NewItProcessToolbarButton';

const keys = {
	[entities.ItSoApplication]: 'itApplications',
	[entities.ItProcess]: 'itProcesses'
};

const ISA315RelateItProcessItApplications = ({
	bodyId,
	className = 'isa315-relate-itprocess-itapplications',
	engagementId,
	callback,
	byId,
	relateEntityAction,
	entityColor,
	entityConfig,
	entityCount,
	entityName,
	entityProps,
	onRemove,
	header,
	dragAndDrop,
	searchPlaceholder,
	noDataPlaceholder,
	selector,
	filterSelector,
	displayToolbar,
	entity,
	onClick,
	entityId,
	onAddEntityClick,
	onFilter
}) => {
	const [filters, setFilters] = useState({});

	const dispatch = useDispatch();

	const fetch = useCallback(() => dispatch(callback(engagementId, bodyId)), [dispatch, engagementId]);

	const onFilterChange = useCallback(
		(name, value) => {
			const key = keys[entityId];
			const newFilters = {...filters, [name]: value};

			if (name === 'searchString' || name === 'filterUnassociated') {
				dispatch(clientSideSearch(`${bodyId}_${key}`, key, newFilters));
				setFilters(newFilters);
			}
			onFilter({
				entityName: keys[entityId],
				filters: newFilters
			});
		},
		[filters]
	);

	const guardArrayResult = (result) => (Array.isArray(result) ? result : []);

	const soFilter = (state) => (id) => Boolean(byId(state, id).isServiceOrganization) === false;
	const allIds = useSelector((state) => guardArrayResult(selector(state, bodyId)).filter(soFilter(state)));

	const filterIds = useSelector((state) => guardArrayResult(filterSelector(state, bodyId)).filter(soFilter(state)));
	const displayedIds = filters.searchString || filters.filterUnassociated ? filterIds : allIds;

	const handleOnSaveComplete = useCallback(() => {
		window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
		if (filters) {
			const key = keys[entityId];
			dispatch(clientSideSearch(`${bodyId}_${key}`, key, filters));
		}
	}, [filters]);

	const createEntityModal = {
		[entities.ItSoApplication]: (
			<NewItApplicationToolbarButton
				label={labels.newItApplication}
				bodyId={bodyId}
				onSaveComplete={handleOnSaveComplete}
			/>
		),
		[entities.ItProcess]: (
			<NewItProcessToolbarButton label={labels.newItProcess} bodyId={bodyId} onSaveComplete={handleOnSaveComplete} />
		)
	};

	return (
		<StyledISA315RelateItProcessItApplications className={`StyledISA315RelateItProcessItApplications ${className}`}>
			<section className="isa315-relate-itprocess-itapplist-title">
				<span className="isa315-relate-itprocess-itapplist-entity-name">{entity}</span>
				{displayedIds && (
					<span className="isa315-relate-itprocess-itapplist-title-count">
						{displayedIds.length > 0 ? '- ' + displayedIds.length : ''}
					</span>
				)}
				<span className="isa315-relate-itprocess-unassociated">
					<UnassociatedEntityCount
						filterIds={filterIds}
						bodyId={`${bodyId}_${keys[entityId]}`}
						onFilterChange={onFilterChange}
						entityId={entityId}
					/>
				</span>
			</section>
			<BodySpecialToolbar
				className="isa315-relate-itprocess-itapplist-title-list-toolbar"
				bodyTypeId={[formBodyType.ISA315ItProcessItApplication]}
				onFilterChange={onFilterChange}
				searchPlaceholder={searchPlaceholder}
				hideSearchBox={allIds && allIds.length === 0 && !filters.searchString}
			>
				{displayToolbar &&
					(allIds?.length > 0 || (filters.searchString && filters.filterUnassociated)) &&
					createEntityModal[entityId]}
			</BodySpecialToolbar>
			<Fetch fetch={fetch}>
				{displayedIds?.length > 0 ? (
					<ISA315RelateItProcessItAppList
						engagementId={engagementId}
						bodyId={bodyId}
						allIds={displayedIds}
						byId={byId}
						relateEntityAction={relateEntityAction}
						entityColor={entityColor}
						entityConfig={entityConfig}
						entityCount={entityCount}
						entityName={entityName}
						entityProps={entityProps}
						entityId={entityId}
						header={header}
						dragAndDrop={dragAndDrop}
						onClick={onClick}
						onRemove={onRemove}
					/>
				) : (
					<NoDataPlaceholder
						className="isa315-relate-itprocess-itapplications-no-data-placeholder"
						placeholder={filters.searchString ? labels.noResultsFound : noDataPlaceholder.text}
						onPlaceholderClickConfig={noDataPlaceholder.config}
					/>
				)}
			</Fetch>
		</StyledISA315RelateItProcessItApplications>
	);
};

ISA315RelateItProcessItApplications.propTypes = {
	className: PropTypes.string,
	engagement: PropTypes.number.isRequired,
	callback: PropTypes.func.isRequired,
	byId: PropTypes.func.isRequired,
	entityColor: PropTypes.string,
	entityConfig: PropTypes.func.isRequired,
	entityCount: PropTypes.func.isRequired,
	entityName: PropTypes.func.isRequired,
	entityProps: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
	searchPlaceholder: PropTypes.string.isRequired,
	header: PropTypes.string.isRequired,
	dragAndDrop: PropTypes.bool,
	noDataPlaceholder: PropTypes.node,
	selector: PropTypes.func.isRequired,
	displayToolbar: PropTypes.bool,
	onAddEntityClick: PropTypes.func.isRequired
};

export default ISA315RelateItProcessItApplications;

const StyledISA315RelateItProcessItApplications = styled.section`
	display: flex;
	flex-direction: column;
	width: calc(50% - var(--px-20)) !important;
	max-height: 100%;
	&.isa315-relate-itprocess-itapplications-body-left {
		margin: 0 var(--px-20) 0 0 !important;
	}
	&.isa315-relate-itprocess-itapplications-body-right {
		margin: 0 0 0 var(--px-20) !important;
	}
	.isa315-relate-itprocess-itapplist-title {
		align-items: center;
		padding-bottom: 0;
		display: flex;
		line-height: normal;
		border-bottom: var(--px-1) solid var(--neutrals-300);
		font-size: var(--px-14);
		font-weight: bold;
		min-height: var(--px-40);
		.isa315-relate-itprocess-itapplist-title-count {
			font-weight: normal;
			padding: 0 var(--px-4);
		}
	}
	.isa315-relate-itprocess-itapplications-no-data-placeholder {
		height: calc(100% - var(--px-60));
		display: flex !important;
		flex-direction: column !important;
		justify-content: center;
		align-items: center;
	}
	.isa315-relate-itprocess-unassociated {
		color: var(--red-600);
		margin-left: auto;
		display: flex;
		align-items: center;
	}
	.isa315-relate-itprocess-itapplist-title-list-toolbar {
		margin-top: var(--px-10) !important;
		.body-special-toolbar-button {
			padding-top: var(--px-10);
		}
	}
`;
